import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DataserviceService } from '../dataservice.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { lastValueFrom } from 'rxjs';

declare const google: any;

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.css'],
})
export class OnboardingComponent implements OnInit {
  @ViewChild('addressInput', { static: false }) addressInput!: ElementRef;
  @ViewChild('imageCanvas', { static: false }) imageCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('signatureCanvas', { static: false }) signatureCanvas!: ElementRef<HTMLCanvasElement>;

  onboardingForm: FormGroup;
  shirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  headshot: File | null = null;
  documents: any;
  currentDocument: any;
  currentUser: any;
  docLoading: boolean = false;
  submitting: boolean = false;

  imageUrl: any;
  badgeImg: any;

  allDocumentsSignedSubject: boolean = false;
  docToken: any;
  docModal: boolean = false;
  docId: any;

  neededQuizzes: any[] = [];

  constructor(
    private fb: FormBuilder,
    private dataService: DataserviceService,
    private router: Router
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const fullName = `${this.currentUser.first_name || ''} ${this.currentUser.last_name || ''}`.trim();
    const formattedDate = this.currentUser.date_of_birth ? this.currentUser.date_of_birth.substring(0, 10) : '';

    this.onboardingForm = this.fb.group({
      name: [{ value: fullName, disabled: true }],
      email: [this.currentUser.email || '', [Validators.required, Validators.email]],
      phone: [{ value: this.currentUser.phone || '', disabled: true }],
      dob: [formattedDate || '', Validators.required],
      address: [this.currentUser.address || '', Validators.required],
      shirtSize: [this.currentUser.shirtSize || '', Validators.required]
    });
  }

  async ngOnInit() {
    this.loadDocuments();
    this.getNeededQuizzes();
    // Try headshotUrl first, then fallback to badge-{userId} path
    const badgePath = this.currentUser?.headshotUrl || (this.currentUser?.id ? `badge-${this.currentUser.id}` : null);
    if (badgePath) {
      try {
        this.badgeImg = await this.dataService.getSupabaseBadge(badgePath);
        this.imageUrl = URL.createObjectURL(this.badgeImg);
      } catch (err) {
        console.warn('Could not load existing badge', err);
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.badgeImg = file;
    this.imageUrl = URL.createObjectURL(file);

  }

  loadDocuments() {
    this.docLoading = true;
    this.dataService.getData('templates/docs-to-sign')
      .subscribe({
        next: (docs) => {
          if (docs == null) {
            this.documents = [];
          }
          else {
            this.documents = docs.filter((doc: any) => doc.status === 'active');
          }
          this.docLoading = false;
        }
      }
      );
  }

  async signDocument(doc: any) {
    this.docModal = true;
    this.docToken = `https://docuseal.com/d/${doc.document_id}`
    this.docId = doc.document_id
  }

  docOpened(ev: any) {
    this.dataService.postData({ doc: this.docId }, `templates/doc-opened`)
      .subscribe({
        next: (res: any) => { }
      })
  }

  docCompleted(ev: any) {
    this.dataService.postData({ docUrl: ev.submission_id, doc: this.docId }, `templates/doc-signed`)
      .subscribe({
        next: (res: any) => {
          this.docModal = false;
          this.docToken = null;
          this.docId = null;
          this.loadDocuments();
        }
      })
  }

  async onSubmit() {
    const allDocsSigned = (this.documents?.length || 0) === 0;
    const allQuizzesDone = (this.neededQuizzes?.length || 0) === 0;
    const hasBadge = !!this.badgeImg;
    const hasExistingBadge = !!this.currentUser?.headshotUrl;
    const isNewUpload = this.badgeImg instanceof File;
  
    if (this.onboardingForm.valid && allDocsSigned && allQuizzesDone && hasBadge) {
      const formValue = this.onboardingForm.getRawValue();
      this.submitting = true;
  
      let headshotUrlToSend: string;
  
      if (hasExistingBadge && !isNewUpload) {
        headshotUrlToSend = this.currentUser.headshotUrl;
      } else if (!isNewUpload && this.currentUser?.id) {
        // Fallback: we loaded from badge-{id} but DB didn't have it - use the path we tried
        headshotUrlToSend = `badge-${this.currentUser.id}`;
      } else {
        // New upload required (first time or denied)
        const filePath = this.buildBadgePath();
        const res = await lastValueFrom(this.dataService.uploadBadgeImg(this.badgeImg, filePath));
        if (!res) {
          alert('Something went wrong uploading your photo');
          this.submitting = false;
          return;
        }
        headshotUrlToSend = filePath;
      }
  
      const userUpdates = {
        email: formValue.email,
        date_of_birth: formValue.dob,
        address: formValue.address,
        shirtSize: formValue.shirtSize,
        headshotUrl: headshotUrlToSend
      };
  
      this.dataService.postData(userUpdates, 'recruiting/onboarding-complete')
        .subscribe({
          next: async (res: any) => {
            if (res?.result == 'success') {
              this.submitting = false;
              const updatedUser = { ...this.currentUser, headshotUrl: headshotUrlToSend };
              this.currentUser = updatedUser;
              localStorage.setItem('user', JSON.stringify(updatedUser));
              this.router.navigate(['/home']);
            } else {
              alert('Something went wrong, please try again or log out and log back in');
            }
          }
        });
    } else {
      alert('Please make sure the form is valid, all documents are signed, quizzes are completed, and a profile photo is uploaded.');
    }
  }

  closeDoc() {
    this.docModal = false;
    this.docToken = null;
    this.docId = null;
  }

  getNeededQuizzes() {
    this.dataService.getData('quizzes/needed-quizzes', { params: { user_id: this.currentUser.id } }).subscribe((res: any) => {
      this.neededQuizzes = res?.data || [];
    });
  }

  takeQuiz(quizId: string) {
    this.router.navigate(['/quiz-taking', quizId]);
  }

  private buildBadgePath(): string {
    const idPart = this.currentUser?.id ? `badge-${this.currentUser.id}` : '';
    const first = (this.currentUser.first_name || '').toString();
    const last = (this.currentUser.last_name || '').toString();
    const namePart = `${first}-${last}-badge`;

    const raw = idPart || namePart || 'badge';
    return raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
